import React, { useCallback, useEffect } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NumberParam, StringParam, useQueryParam, useQueryParams, withDefault } from "use-query-params";

import { Table } from "antd";
import { SorterResult } from "antd/lib/table/interface";

import { Page } from "../../components/page";
import { PageHeader } from "../../components/page_header";
import { Searcher } from "../../components/searcher";
import { CurrencySelector, DateRange, MarketSelector } from "../../components/selectors";
import { ColumnTitle, TableNotification } from "../../components/table";
import { ReportFormat } from "../../store/common_types";
import { selectCurrency } from "../../store/currencies/actions";
import { RootState } from "../../store/root_reducer";
import { reportGeneratorTaskCreateRequest, tableFetchRequest } from "../../store/routes/actions";
import { RouteItem } from "../../store/routes/types";
import { MomentParam } from "../../utils/moment_param";
import { formatNumber, getSortOrder } from "../../utils/table";

export const RoutesPage: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["routes_page", "common"]);

  // -- Hooks Start --
  const [dateRange, setDateRange] = useQueryParams({
    from: withDefault(MomentParam, moment.utc().startOf("day").subtract(1, "week").add(1, "day")),
    to: withDefault(MomentParam, moment.utc().startOf("day")),
  });
  const [pagination, setPagination] = useQueryParams({
    pageSize: withDefault(NumberParam, 10),
    page: withDefault(NumberParam, 1),
  });
  const [market, setMarket] = useQueryParam("market", withDefault(StringParam, undefined));
  const [search, setSearch] = useQueryParam("search", withDefault(StringParam, undefined));
  const [ordering, setOrdering] = useQueryParam("ordering", withDefault(StringParam, "-totalSearches"));
  // -- Hooks End --

  // -- Stores Start --
  const reportState = useSelector((state: RootState) => state.Routes);
  const markets = useSelector((state: RootState) => state.Markets.markets);
  const selectedGate = useSelector((state: RootState) => state.User.selectedGate);
  const currency = useSelector((state: RootState) => state.Currencies.selectedCurrency);
  // -- Stores End --

  // -- Custom Handlers Start --
  const chooseCurrency = (value: string) => dispatch(selectCurrency({ currency: value }));
  const chooseDateRange = (value: DateRange) => {
    setDateRange(value);
    setPagination({ ...pagination, page: 1 }, "replaceIn");
  };
  const chooseMarket = (value?: string) => {
    setPagination({ ...pagination, page: 1 });
    setMarket(value, "replaceIn");
  };
  // -- Custom Handlers End --

  // -- Hook Effects Start --
  useEffect(() => {
    setDateRange((state) => state, "replaceIn");
    setPagination((state) => state, "replaceIn");
    setOrdering((state) => state, "replaceIn");
    setMarket((state) => state, "replaceIn");
    setSearch((state) => state, "replaceIn");
  }, [setDateRange, setMarket, setOrdering, setPagination, setSearch]);

  const fetch = useCallback(
    () =>
      dispatch(
        tableFetchRequest({
          ...pagination,
          ...dateRange,
          market,
          currency,
          ordering,
          search,
          gateId: selectedGate.id,
        })
      ),
    [dispatch, dateRange, pagination, market, currency, ordering, selectedGate, search]
  );

  const downloadReport = useCallback(
    (reportFormat: ReportFormat) => {
      dispatch(
        reportGeneratorTaskCreateRequest({
          ...dateRange,
          market,
          currency,
          ordering,
          search,
          reportFormat,
          gateId: selectedGate.id,
        })
      );
    },
    [dispatch, market, currency, ordering, dateRange, selectedGate, search]
  );

  useEffect(() => {
    fetch();
  }, [fetch]);
  // -- Hook Effects End --

  const fields = [
    "direction",
    "totalSearches",
    "totalClicks",
    "totalUniqueClicks",
    "ctr",
    "totalPaidBookings",
    "str",
    "totalTurnover",
    "avgCheck",
    "totalErrors",
    "totalZeros",
    "totalTimeouts",
  ] as Array<keyof RouteItem>;
  const columns = fields.map((field, i) => ({
    title: (
      <ColumnTitle
        title={t(`table.columns.${field}.title`)}
        helpText={t(`table.columns.${field}.help`, { currency })}
      />
    ),
    key: field,
    dataIndex: field,
    sorter: true,
    sortOrder: getSortOrder(field, ordering),
    render: (text: string, record: RouteItem) => (field !== "direction" ? formatNumber(record[field]) : text),
  }));

  const handleSearch = (value: string) => {
    setSearch(value || undefined);
    setPagination((state) => ({ ...state, page: 1 }));
  };

  return (
    <Page title={t("page.title")} description={t("page.description")} reportTTL={60}>
      <PageHeader
        onReload={fetch}
        dateRange={dateRange}
        isLoading={reportState.isLoading}
        onDateRangeSelect={chooseDateRange}
        onDownloadReport={downloadReport}
        reportDownloadDisabled={reportState.count === 0}
      >
        <MarketSelector value={market} markets={markets} onSelect={chooseMarket} />
        <CurrencySelector value={currency} onSelect={chooseCurrency} />
        <Searcher loading={reportState.isLoading} onSearch={handleSearch} defaultValue={search} />
      </PageHeader>
      <TableNotification />
      <Table
        bordered
        size="small"
        columns={columns}
        dataSource={reportState.items}
        loading={reportState.isLoading}
        rowKey={(item, index) => `routes-row-${index}`}
        pagination={{
          total: reportState.count,
          current: pagination.page,
          pageSize: pagination.pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "100"],
          onChange: (page, pageSize) => {
            if (page && pageSize) {
              setPagination((currentPagination) => ({
                ...currentPagination,
                pageSize,
                page: pageSize !== currentPagination.pageSize ? 1 : page,
              }));
            }
          },
        }}
        showSorterTooltip={false}
        sortDirections={["ascend", "descend", "ascend"]}
        onChange={(_, filters, sorter: SorterResult<RouteItem> | SorterResult<RouteItem>[], extra) => {
          if (!Array.isArray(sorter)) {
            setOrdering(`${sorter.order === "descend" ? "-" : ""}${sorter.column?.dataIndex}`);
          }
        }}
      />
    </Page>
  );
};
